import React, { createContext, useCallback, useContext, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Remplaçant de `window.confirm()` qui fonctionne dans les iframes.
 *
 * Usage dans un composant :
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "...", description: "...", destructive: true });
 *   if (!ok) return;
 *
 * Le <ConfirmProvider> est déjà monté dans App.js.
 */
const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {}, resolver: null });

  const confirm = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        setState({ open: true, opts, resolver: resolve });
      }),
    []
  );

  const close = (result) => {
    state.resolver?.(result);
    setState({ open: false, opts: {}, resolver: null });
  };

  const {
    title = "Confirmer ?",
    description = "",
    confirmText = "Confirmer",
    cancelText = "Annuler",
    destructive = false,
  } = state.opts;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => close(false)} data-testid="confirm-dialog-cancel">
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              className={destructive ? "bg-[#C05C47] hover:bg-[#A64A37]" : undefined}
              data-testid="confirm-dialog-confirm"
            >
              {confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
